import { RouteObject } from "react-router-dom";
import ReservePage from "./pages/ReservePage";
import AuthPage from "./pages/AuthPage";
import GuardedRoute from "./components/GuardedRoute";
import HeaderLinksPage from "./pages/HeaderLinksPage";
import HeaderLinkPage from "./pages/HeaderLinkPage";
import UserPage from "./pages/UserPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import OrganizationPage from "./pages/OrganizationPage";
import StationsPage from "./pages/StationsPage";
import SettingsPage from "./pages/SettingsPage";
import { UserRole } from "./shared/IUser";
import LogoutPage from "./pages/LogoutPage";
import ReservationsPage from "./pages/ReservationsPage";
import ReservationPage from "./pages/ReservationPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import InfoWidgetsPage from "./pages/InfoWidgetsPage";
import InfoWidgetPage from "./pages/InfoWidgetPage";
import AvailabilityDebugPage from "./pages/AvailabilityDebugPage";
import ApiDocsPage from "./pages/ApiDocsPage";

export enum ERoute {
  reserve = "reserve",
  auth = "auth",
  user = "users/:id",
  logout = "logout",
  reservations = "reservations",
  reservation = "reservations/:refNo/:irn?",
  headerLinks = "header-links",
  headerLink = "header-links/:id",
  organizations = "organizations",
  organization = "organizations/:id",
  stations = "stations",
  settings = "settings",
  auditTrails = "audit-trails",
  infoWidgets = "info-widgets",
  infoWidget = "info-widgets/:id",
  availabilityDebug = "availability-debug",
  apiDocs = "api-docs",
}

interface Params {
  [key: string]: string;
}

export class Route {
  static reserve = `/${ERoute.reserve}`;
  static auth = `/${ERoute.auth}`;
  static user = (id: string) => `/${this.withParams(ERoute.user, { id })}`;
  static logout = `/${ERoute.logout}`;
  static reservations = `/${ERoute.reservations}`;
  static reservation = (refNo: string, irn?: string) => irn ? `/${this.withParams(ERoute.reservation, { refNo, irn })}`.replace('?', '') : `/${ERoute.reservation.replace(':refNo', encodeURIComponent(refNo)).replace('/:irn?', '')}`;
  static headerLinks = `/${ERoute.headerLinks}`;
  static headerLink = (id: string) => `/${this.withParams(ERoute.headerLink, { id })}`;
  static organizations = `/${ERoute.organizations}`;
  static organization = (id: string) => `/${this.withParams(ERoute.organization, { id })}`;
  static stations = `/${ERoute.stations}`;
  static settings = `/${ERoute.settings}`;
  static auditTrails = `/${ERoute.auditTrails}`;
  static infoWidgets = `/${ERoute.infoWidgets}`;
  static infoWidget = (id: string) => `/${this.withParams(ERoute.infoWidget, { id })}`;
  static availabilityDebug = `/${ERoute.availabilityDebug}`;
  static apiDocs = `/${ERoute.apiDocs}`;
  static userAddWithOrganization = (organizationId: string) => `${this.user("add")}?organizationId=${organizationId}`;

  private static withParams(route: ERoute, params: Params): string {
    let routeWithParams: string = route;
    for(let key in params) {
      if (params[key] !== undefined && params[key] !== null) {
        routeWithParams = routeWithParams.replace(`:${key}`, encodeURIComponent(params[key]));
      }
    }
    return routeWithParams;
  }
}

export type TRoute = {
  roles: UserRole[];
} & RouteObject;

export interface IRoutes {
  [key: string]: TRoute;
}

const BASIC_ROLE: UserRole[] = ["AGENT", "ADMIN", "SUPERADMIN"];
const ADMIN_ROLE: UserRole[] = ["ADMIN", "SUPERADMIN"];

export const routes: IRoutes = {
  [ERoute.reserve]: {
    element: <GuardedRoute route={ReservePage} />,
    roles: BASIC_ROLE,
  },
  [ERoute.auth]: {
    element: <AuthPage />,
    roles: [],
  },
  [ERoute.user]: {
    element: <GuardedRoute route={UserPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.logout]: {
    element: <LogoutPage />,
    roles: [],
  },
  [ERoute.reservations]: {
    element: <GuardedRoute route={ReservationsPage} />,
    roles: BASIC_ROLE,
  },
  [ERoute.reservation]: {
    element: <GuardedRoute route={ReservationPage} />,
    roles: BASIC_ROLE,
  },
  [ERoute.headerLinks]: {
    element: <GuardedRoute route={HeaderLinksPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.headerLink]: {
    element: <GuardedRoute route={HeaderLinkPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.organizations]: {
    element: <GuardedRoute route={OrganizationsPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.organization]: {
    element: <GuardedRoute route={OrganizationPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.stations]: {
    element: <GuardedRoute route={StationsPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.settings]: {
    element: <GuardedRoute route={SettingsPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.auditTrails]: {
    element: <GuardedRoute route={AuditTrailPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.infoWidgets]: {
    element: <GuardedRoute route={InfoWidgetsPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.infoWidget]: {
    element: <GuardedRoute route={InfoWidgetPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.availabilityDebug]: {
    element: <GuardedRoute route={AvailabilityDebugPage} />,
    roles: ADMIN_ROLE,
  },
  [ERoute.apiDocs]: {
    element: <GuardedRoute route={ApiDocsPage} />,
    roles: ADMIN_ROLE,
  },
};